import React from 'react';
import styled from 'styled-components';
import { PhotoAsset, PhotoArtifactTab } from '../../types.ts/story';
import { Circle } from '../../styles/mainStyle';
import CheckIcon from '../../svgs/CheckIcon';
import EnlargedImageRenderProp from '../common/EnlargedImageRenderProp';
import { AuthorAttribution, UnsplashAttribution } from '../common/UnsplashAttributions';

interface PhotoGridProps {
  resource: PhotoAsset[] | undefined;
  tab: PhotoArtifactTab;
  selectedId?: string;
  unsplashSearch?: string;
  unsplashLoading?: boolean;
  onSelectResource: (resource: PhotoAsset, tab: PhotoArtifactTab) => void;
  onLoadMore?: () => void;
}

const SMALL_PHOTO_HEIGHT = 100;

const PhotoGrid: React.FC<PhotoGridProps> = ({
  resource,
  tab,
  selectedId,
  unsplashSearch,
  unsplashLoading,
  onSelectResource,
  onLoadMore,
}) => {
  const renderAssetLevelAttribution = (asset: PhotoAsset) => {
    if (!asset.attribution) return null;

    switch (tab) {
      case PhotoArtifactTab.stock:
        return (
          <AuthorAttribution
            author={asset.attribution.author}
            link={asset.attribution.link}
          />
        );
      default:
        return null;
    }
  };

  const renderTabLevelAttribution = () => {
    if (!resource?.some((a) => a.attribution)) return null;

    switch (tab) {
      case PhotoArtifactTab.stock:
        return (
          <AttributionRow>
            <UnsplashAttribution />
          </AttributionRow>
        );
      default:
        return null;
    }
  };

  if (!resource) {
    return (
      <Container>
        <NoResource>
          Search the stock library to add photos to the story
        </NoResource>
      </Container>
    );
  }

  if (!resource.length) {
    return (
      <Container>
        <NoResource>You have no matching assets</NoResource>
      </Container>
    );
  }

  return (
    <Container>
      {resource.map((r) => (
        <ResourceElementWrapper key={r.id}>
          <EnlargedImageRenderProp
            src={r.src}
            smallPhotoHeight={SMALL_PHOTO_HEIGHT}
          >
            {({ enlargedState, setEnlargedState, renderEnlargedImage }) => (
              <>
                <ResourceElement
                  onClick={() => onSelectResource(r, tab)}
                  onMouseEnter={(e) => {
                    setEnlargedState(e.currentTarget.getBoundingClientRect());
                  }}
                  onMouseMove={(e) => {
                    if (!enlargedState) {
                      setEnlargedState(e.currentTarget.getBoundingClientRect());
                    }
                  }}
                  onMouseLeave={() => {
                    setEnlargedState(null);
                  }}
                >
                  <Circle isSelected={selectedId === r.id}>
                    {selectedId === r.id && <CheckIcon />}
                  </Circle>
                  <ResourceImage loading="lazy" srcSet={r.src} />
                </ResourceElement>
                {renderAssetLevelAttribution(r)}
                {enlargedState && renderEnlargedImage()}
              </>
            )}
          </EnlargedImageRenderProp>
        </ResourceElementWrapper>
      ))}
      {renderTabLevelAttribution()}
      {tab === PhotoArtifactTab.stock &&
        unsplashSearch &&
        resource.length > 0 && (
          <LoadMoreButton
            disabled={unsplashLoading}
            onClick={onLoadMore}
          >
            Load more photos
          </LoadMoreButton>
        )}
    </Container>
  );
};

export default PhotoGrid;

const Container = styled.div`
  margin-top: 16px;
  width: 100%;
  display: grid;
  grid-template-columns: repeat(2, 1fr);
  row-gap: 5px;
  column-gap: 16px;
`;

const ResourceElementWrapper = styled.div`
  &:not(:last-child) {
    margin-bottom: 10px;
  }
`;

const ResourceElement = styled.div`
  position: relative;
  display: flex;
  flex-direction: column;
  cursor: pointer;
  height: ${SMALL_PHOTO_HEIGHT}px;
  border-radius: 10px;
  border: 1px solid #484848;
`;

const ResourceImage = styled.img`
  margin: auto;
  min-height: ${SMALL_PHOTO_HEIGHT}px;
  width: 100%;
  height: 100%;
  object-fit: cover;
  border-radius: 8px;
  object-position: top;
  box-shadow:
    0 2px 4px rgba(0, 0, 0, 0.1),
    0 4px 8px rgba(0, 0, 0, 0.1);
`;

const NoResource = styled.div`
  margin-top: 10px;
  grid-column: 1/3;
  text-align: center;
`;

const LoadMoreButton = styled.button`
  grid-column: 1/3;
  display: flex;
  padding: 12px 16px;
  justify-content: center;
  align-items: center;
  gap: 8px;
  border-radius: 10px;
  background-color: #03041a;
  color: #17c964;
  border: 1px solid #17c964;
  cursor: pointer;
  font-weight: 700;
  font-size: 14px;
  outline: 0;
  transition: 0.2s;
  &:disabled {
    cursor: not-allowed;
    opacity: 0.6;
  }
`;

const AttributionRow = styled.div`
  grid-column: 1/3;
  margin: 6px 0 16px 0;
  display: flex;
  justify-content: center;
`;